import React, {FC, useState, useEffect} from "react";
import {ErrorMessage, Field, Form, Formik} from "formik";
import Select from "react-select";
import Input from "../../../common/form-elements/input/input";
import FormErrorMessage from "../../../common/form-elements/form-error-message/form-error-message";
import Checkbox from "../../../common/form-elements/checkbox/checkbox";

// Services
import DashboardService from "../../../../services/dashboard-service";

// Types and Constants
import {IROIWidgetConfig, ITaskConfig} from "./models/roi-widget.props";

// Styles
import "./roi-configuration-form.scss";
import {
    INITIAL_VALUES,
    METRIC_OPTIONS,
    VISUALIZATION_OPTIONS,
    COMPARE_TO_OPTIONS,
    REPORT_FORMAT_OPTIONS,
    CURRENCY_OPTIONS,
    DEFAULT_TASK_TYPES,
} from "./constants";

interface IROIConfigurationFormProps {
    initialValues: IROIWidgetConfig;
    tempConfigData: IROIWidgetConfig;
    onFormSubmit: (values: IROIWidgetConfig) => void;
    onCancel: () => void;
    handleFormFieldChange: (field: string, value: any) => void;
}

const ROIConfigurationForm: FC<IROIConfigurationFormProps> = ({
    initialValues,
    tempConfigData,
    onFormSubmit,
    onCancel,
    handleFormFieldChange,
}) => {
    const [activeTab, setActiveTab] = useState<"general" | "manual_task_effort">("general");
    const [selectAllTasks, setSelectAllTasks] = useState<boolean | null>(null);
    const [taskTypes, setTaskTypes] = useState<ITaskConfig[]>([]);
    const [currencyOptions, setCurrencyOptions] = useState<any[]>([]);
    const [isLoadingData, setIsLoadingData] = useState<boolean>(true);

    // Load dynamic data from backend and saved configuration
    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoadingData(true);

                // Load currency options
                setCurrencyOptions(CURRENCY_OPTIONS);

                // Load task types
                setTaskTypes(DEFAULT_TASK_TYPES);
            } catch (error) {
                console.error("Error loading ROI configuration data:", error);
                // Set defaults on error
                setCurrencyOptions(CURRENCY_OPTIONS);
                setTaskTypes(DEFAULT_TASK_TYPES);
            } finally {
                setIsLoadingData(false);
            }
        };

        loadData();
    }, []);

    const handleSubmit = (values: IROIWidgetConfig) => {
        onFormSubmit(values);
    };

    const validate = (values: IROIWidgetConfig) => {
        const errors: any = {};

        if (!values.title) {
            errors.title = "Title is required";
        } else if (values.title.length > 50) {
            errors.title = "Title must be less than 50 characters";
        }

        // Validate global settings and task configuration
        if (
            !values.globalSettings?.averageHourlyRate ||
            values.globalSettings.averageHourlyRate < 0
        ) {
            errors.averageHourlyRate = "Average hourly rate must be a positive number";
        }

        const selectedTasks = values.taskConfiguration?.filter((t) => t.enabled) || [];
        if (selectedTasks.length === 0) {
            errors.taskConfiguration = "Please select at least one task";
        }

        // Validate time values for selected tasks
        selectedTasks.forEach((task, index) => {
            if (task.timePerTask < 0) {
                errors[`task_${task.id}_time`] = "Time per task must be non-negative";
            }
        });

        return errors;
    };

    const getVisualizationOptions = (selectedMetric: string) => {
        if (selectedMetric === "top_automated_tasks") {
            return []; // Disable all visualization options
        }
        return VISUALIZATION_OPTIONS;
    };

    // Calculate the "Select All" state based on individual task states
    const calculateSelectAllState = (taskConfiguration: ITaskConfig[]) => {
        if (!taskConfiguration || taskConfiguration.length === 0) {
            return false;
        }

        const enabledTasks = taskConfiguration.filter((task) => task.enabled === true);

        if (enabledTasks.length === 0) {
            return false; // None selected
        } else if (enabledTasks.length === taskConfiguration.length) {
            return true; // All selected
        } else {
            return null; // Some selected (indeterminate state)
        }
    };

    // Update "Select All" state when task configuration changes
    const updateSelectAllState = (taskConfiguration: ITaskConfig[]) => {
        const selectAllState = calculateSelectAllState(taskConfiguration);
        setSelectAllTasks(selectAllState);
    };

    const handleSelectAllTasks = (
        values: IROIWidgetConfig,
        setFieldValue: any,
        selectAll: boolean
    ) => {
        const updatedTasks = (values.taskConfiguration || []).map((task) => ({
            ...task,
            enabled: selectAll,
        }));
        setFieldValue("taskConfiguration", updatedTasks);
        setSelectAllTasks(selectAll);
        handleFormFieldChange("taskConfiguration", updatedTasks);
    };

    const initializeTaskConfiguration = (values: IROIWidgetConfig) => {
        if (!values.taskConfiguration || values.taskConfiguration.length === 0) {
            return taskTypes;
        }
        return values.taskConfiguration;
    };

    // Update "Select All" state when initial values change
    useEffect(() => {
        if (initialValues?.taskConfiguration) {
            updateSelectAllState(initialValues.taskConfiguration);
        }
    }, [initialValues?.taskConfiguration]);

    if (isLoadingData) {
        return (
            <div className="glu-modal__body" style={{padding: "40px", textAlign: "center"}}>
                <div>Loading configuration options...</div>
            </div>
        );
    }

    // Ensure proper initialization of all required fields
    const safeInitialValues = {
        ...INITIAL_VALUES,
        ...tempConfigData,
        // Ensure critical fields are always set from INITIAL_VALUES if not present in tempConfigData
        metric: tempConfigData.metric || INITIAL_VALUES.metric,
        visualizationMode: tempConfigData.visualizationMode || INITIAL_VALUES.visualizationMode,
        compareTo: tempConfigData.compareTo || INITIAL_VALUES.compareTo,
        reportFormat: tempConfigData.reportFormat || INITIAL_VALUES.reportFormat,
        globalSettings: {
            averageHourlyRate: 0,
            currency: {value: "USD", label: "USD ($)"},
            ...(tempConfigData.globalSettings || {}),
        },
        taskConfiguration: initializeTaskConfiguration(tempConfigData),
    };

    return (
        <Formik
            initialValues={safeInitialValues}
            validate={validate}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            {({values, setFieldValue, isSubmitting, errors}) => (
                <Form className="roi-configuration-form">
                    <div className="glu-modal__body">
                        {/* Tab Navigation */}
                        <div className="roi-tabs">
                            <div className="roi-tabs__nav">
                                <button
                                    type="button"
                                    className={`roi-tabs__nav-item ${
                                        activeTab === "general" ? "roi-tabs__nav-item--active" : ""
                                    }`}
                                    onClick={() => setActiveTab("general")}
                                >
                                    General
                                </button>
                                <button
                                    type="button"
                                    className={`roi-tabs__nav-item ${
                                        activeTab === "manual_task_effort"
                                            ? "roi-tabs__nav-item--active"
                                            : ""
                                    }`}
                                    onClick={() => setActiveTab("manual_task_effort")}
                                >
                                    Manual task effort
                                </button>
                            </div>

                            {/* Tab Content */}
                            <div className="roi-tabs__content">
                                {activeTab === "general" && (
                                    <div className="glu-content__section">
                                        {/* Widget Title */}
                                        <div className="glu-form-row">
                                            <div className="glu-label">Widget title</div>
                                            <Field name="title">
                                                {({field, form}: any) => (
                                                    <Input
                                                        {...field}
                                                        className="glu-form-input-pad"
                                                        placeholder="Enter widget title"
                                                        autoFocus
                                                        onChange={(e: string) => {
                                                            const newValue = e;
                                                            form.setFieldValue("title", newValue);
                                                            handleFormFieldChange(
                                                                "title",
                                                                newValue
                                                            );
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                            <ErrorMessage
                                                name="title"
                                                component={FormErrorMessage}
                                            />
                                        </div>

                                        {/* Metric */}
                                        <div className="glu-form-row">
                                            <div className="glu-label">Metric</div>
                                            <div className="glu-radio-group">
                                                {METRIC_OPTIONS.map((option) => (
                                                    <div
                                                        key={option.value}
                                                        className="glu-radio-item"
                                                    >
                                                        <Field
                                                            type="radio"
                                                            name="metric"
                                                            value={option.value}
                                                            checked={
                                                                !values.metric
                                                                    ? option.value ===
                                                                      "cost_savings"
                                                                    : values.metric?.value ===
                                                                      option.value
                                                            }
                                                            onChange={() => {
                                                                const selectedOption = option;
                                                                setFieldValue(
                                                                    "metric",
                                                                    selectedOption
                                                                );
                                                                handleFormFieldChange(
                                                                    "metric",
                                                                    selectedOption
                                                                );

                                                                // Reset visualization mode if top automated tasks is selected
                                                                if (
                                                                    selectedOption.value ===
                                                                    "top_automated_tasks"
                                                                ) {
                                                                    setFieldValue(
                                                                        "visualizationMode",
                                                                        null
                                                                    );
                                                                    handleFormFieldChange(
                                                                        "visualizationMode",
                                                                        null
                                                                    );
                                                                }
                                                            }}
                                                            className="glu-radio"
                                                        />
                                                        <label className="glu-radio-label glu-radio-label-with-description">
                                                            <span className="glu-radio-text">
                                                                {option.label}
                                                            </span>
                                                            <span className="glu-radio-description">
                                                                <i className="g-icon g-icon-note"></i>
                                                                {option.value === "cost_savings" &&
                                                                    "Total estimated cost savings based on configured labor rates"}
                                                                {option.value ===
                                                                    "tasks_automated" &&
                                                                    "Total tasks completed via automation in the selected period"}
                                                                {option.value === "time_saved" &&
                                                                    "Estimated hours saved through automation"}
                                                                {option.value ===
                                                                    "top_automated_tasks" &&
                                                                    "Top 5 automated tasks by time spent"}
                                                            </span>
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Visualization */}
                                        <div className="glu-form-row">
                                            <div className="glu-label">Visualization</div>
                                            {values.metric?.value === "top_automated_tasks" ? (
                                                <div className="glu-form-control--disabled">
                                                    Visualization options are disabled for "Top
                                                    automated tasks" metric
                                                </div>
                                            ) : (
                                                <div className="glu-radio-group">
                                                    {VISUALIZATION_OPTIONS.map((option) => (
                                                        <div
                                                            key={option.value}
                                                            className="glu-radio-item"
                                                        >
                                                            <Field
                                                                name="visualizationMode"
                                                                type="radio"
                                                                value={option.value}
                                                                checked={
                                                                    !values.visualizationMode
                                                                        ? option.value === "numeric"
                                                                        : values.visualizationMode
                                                                              ?.value ===
                                                                          option.value
                                                                }
                                                                onChange={() => {
                                                                    setFieldValue(
                                                                        "visualizationMode",
                                                                        option
                                                                    );
                                                                    handleFormFieldChange(
                                                                        "visualizationMode",
                                                                        option
                                                                    );
                                                                }}
                                                                className="glu-radio"
                                                            />
                                                            <label className="glu-radio-label">
                                                                {option.label}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Compare to */}
                                        <div className="glu-form-row">
                                            <Checkbox
                                                id="compareTo.enabled"
                                                isChecked={values.compareTo?.enabled || false}
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    const enabled = e.target.checked;
                                                    const compareToValue = {
                                                        enabled,
                                                        option: enabled
                                                            ? COMPARE_TO_OPTIONS[0]
                                                            : undefined,
                                                    };
                                                    setFieldValue("compareTo", compareToValue);
                                                    handleFormFieldChange(
                                                        "compareTo",
                                                        compareToValue
                                                    );
                                                }}
                                            >
                                                Compare to
                                            </Checkbox>

                                            <div style={{marginTop: "10px"}}>
                                                <div className="glu-radio-group">
                                                    {COMPARE_TO_OPTIONS.map((option) => (
                                                        <div
                                                            key={option.value}
                                                            className={
                                                                values.compareTo?.enabled
                                                                    ? "glu-radio-item"
                                                                    : "glu-radio-item glu-radio-item--disabled"
                                                            }
                                                        >
                                                            <Field
                                                                name="compareTo.option"
                                                                type="radio"
                                                                value={option.value}
                                                                checked={
                                                                    values.compareTo?.option
                                                                        ?.value === option.value
                                                                }
                                                                onChange={() => {
                                                                    const compareToValue = {
                                                                        ...values.compareTo,
                                                                        option: option,
                                                                    };
                                                                    setFieldValue(
                                                                        "compareTo",
                                                                        compareToValue
                                                                    );
                                                                    handleFormFieldChange(
                                                                        "compareTo",
                                                                        compareToValue
                                                                    );
                                                                }}
                                                                className="glu-radio"
                                                            />
                                                            <label className="glu-radio-label">
                                                                {option.label}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Report download */}
                                        <div className="glu-form-row">
                                            <div className="glu-label">Report download</div>
                                            <div className="glu-radio-group">
                                                {REPORT_FORMAT_OPTIONS.map((option) => (
                                                    <div key={option.value}>
                                                        <Field
                                                            name="reportFormat"
                                                            type="radio"
                                                            value={option.value}
                                                            checked={
                                                                values.reportFormat?.value ===
                                                                option.value
                                                            }
                                                            onChange={() => {
                                                                setFieldValue(
                                                                    "reportFormat",
                                                                    option
                                                                );
                                                                handleFormFieldChange(
                                                                    "reportFormat",
                                                                    option
                                                                );
                                                            }}
                                                            className="glu-radio"
                                                        />
                                                        <label className="glu-radio-label">
                                                            {option.label} report
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {activeTab === "manual_task_effort" && (
                                    <div className="glu-content__section">
                                        <div className="glu-form-row">
                                            <div className="glu-label glu-label-main">
                                                Global settings
                                            </div>
                                        </div>
                                        {/* Global Settings */}
                                        <div className="glu-form-row">
                                            <div className="glu-label">Average hourly rate</div>
                                            <input
                                                type="number"
                                                className="glu-form-input-pad glu-input-numeric"
                                                placeholder="0"
                                                min="0"
                                                step="0.01"
                                                value={
                                                    values.globalSettings?.averageHourlyRate || ""
                                                }
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>
                                                ) => {
                                                    try {
                                                        const inputValue = e.target?.value || "";
                                                        const value =
                                                            inputValue === ""
                                                                ? 0
                                                                : parseFloat(inputValue) || 0;

                                                        // Ensure globalSettings exists with safe defaults
                                                        const currentGlobalSettings =
                                                            values.globalSettings || {
                                                                averageHourlyRate: 0,
                                                                currency: {
                                                                    value: "USD",
                                                                    label: "USD ($)",
                                                                },
                                                            };

                                                        const updatedGlobalSettings = {
                                                            ...currentGlobalSettings,
                                                            averageHourlyRate: value,
                                                        };

                                                        setFieldValue(
                                                            "globalSettings",
                                                            updatedGlobalSettings
                                                        );
                                                        handleFormFieldChange(
                                                            "globalSettings",
                                                            updatedGlobalSettings
                                                        );
                                                    } catch (error: any) {
                                                        console.error(
                                                            "Error in hourly rate onChange:",
                                                            error
                                                        );
                                                        console.error("Error stack:", error?.stack);
                                                    }
                                                }}
                                            />
                                            <ErrorMessage
                                                name="averageHourlyRate"
                                                component={FormErrorMessage}
                                            />
                                        </div>

                                        <div className="glu-form-row">
                                            <div className="glu-label">Currency</div>
                                            <Select
                                                value={values.globalSettings?.currency}
                                                onChange={(selectedOption: any) => {
                                                    // Ensure globalSettings exists with safe defaults
                                                    const currentGlobalSettings =
                                                        values.globalSettings || {
                                                            averageHourlyRate: 0,
                                                            currency: {
                                                                value: "USD",
                                                                label: "USD ($)",
                                                            },
                                                        };

                                                    const updatedGlobalSettings = {
                                                        ...currentGlobalSettings,
                                                        currency: selectedOption,
                                                    };

                                                    setFieldValue(
                                                        "globalSettings",
                                                        updatedGlobalSettings
                                                    );
                                                    handleFormFieldChange(
                                                        "globalSettings",
                                                        updatedGlobalSettings
                                                    );
                                                }}
                                                options={currencyOptions}
                                                placeholder="Select currency"
                                                className="glu-select glu-form-input-pad"
                                                classNamePrefix="react-select"
                                            />
                                        </div>

                                        {/* Task Configuration */}
                                        <div className="glu-form-row">
                                            <div className="glu-label glu-label-main">
                                                Task configuration
                                            </div>
                                            <div
                                                style={{
                                                    marginBottom: "1rem",
                                                    display: "flex",
                                                    alignItems: "self-start",
                                                    color: "#666",
                                                    fontSize: "1.4rem",
                                                }}
                                            >
                                                <span
                                                    className="g-icon g-icon-note"
                                                    style={{
                                                        marginRight: "0.5rem",
                                                        fontSize: "1.4rem",
                                                    }}
                                                ></span>
                                                Estimate the amount of time (in hours) to complete
                                                the task on a single device
                                            </div>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    color: "#666",
                                                    fontSize: "1.4rem",
                                                    marginLeft: "2rem",
                                                    marginBottom: "2rem",
                                                }}
                                            >
                                                You need to have at least one selected task
                                            </div>
                                            {/* Header row */}
                                            <div
                                                className="glu-task-config-header"
                                                style={{
                                                    marginBottom: "0",
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <div className="glu-col-11">
                                                    <Checkbox
                                                        id="selectAllTasks"
                                                        isChecked={selectAllTasks === true}
                                                        onChange={(
                                                            e: React.ChangeEvent<HTMLInputElement>
                                                        ) => {
                                                            handleSelectAllTasks(
                                                                values,
                                                                setFieldValue,
                                                                e.target.checked
                                                            );
                                                        }}
                                                    >
                                                        Select all
                                                    </Checkbox>
                                                </div>
                                                <div
                                                    className="glu-col-9"
                                                    style={{textAlign: "center"}}
                                                >
                                                    <span className="glu-label glu-label__time-per-task">
                                                        Time per task (hrs)
                                                    </span>
                                                </div>
                                            </div>

                                            {values.taskConfiguration?.map(
                                                (task: ITaskConfig, index: number) => (
                                                    <div
                                                        key={task.id}
                                                        className={
                                                            index % 2 === 0
                                                                ? "glu-task-config-row glu-task-config-row-odd"
                                                                : "glu-task-config-row"
                                                        }
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            marginBottom: "0",
                                                        }}
                                                    >
                                                        <div className="glu-col-17">
                                                            <Checkbox
                                                                id={`task-${task.id}-${index}`}
                                                                isChecked={task.enabled === true}
                                                                onChange={(
                                                                    e: React.ChangeEvent<HTMLInputElement>
                                                                ) => {
                                                                    const updatedTasks = [
                                                                        ...(values.taskConfiguration ||
                                                                            []),
                                                                    ];
                                                                    updatedTasks[index] = {
                                                                        ...updatedTasks[index],
                                                                        enabled: e.target.checked,
                                                                    };
                                                                    setFieldValue(
                                                                        "taskConfiguration",
                                                                        updatedTasks
                                                                    );
                                                                    handleFormFieldChange(
                                                                        "taskConfiguration",
                                                                        updatedTasks
                                                                    );
                                                                    // Update "Select All" state based on individual checkboxes
                                                                    updateSelectAllState(
                                                                        updatedTasks
                                                                    );
                                                                }}
                                                            >
                                                                {task.name}
                                                            </Checkbox>
                                                        </div>
                                                        <div className="glu-col-8">
                                                            <input
                                                                type="number"
                                                                className="glu-form-control roi-activities-number-input glu-input-numeric"
                                                                placeholder="0"
                                                                min="0"
                                                                step="0.01"
                                                                disabled={!task.enabled}
                                                                value={
                                                                    task.timePerTask === 0
                                                                        ? ""
                                                                        : task.timePerTask
                                                                }
                                                                onChange={(
                                                                    e: React.ChangeEvent<HTMLInputElement>
                                                                ) => {
                                                                    const inputValue =
                                                                        e.target.value;
                                                                    const updatedTasks = [
                                                                        ...(values.taskConfiguration ||
                                                                            []),
                                                                    ];
                                                                    updatedTasks[index] = {
                                                                        ...updatedTasks[index],
                                                                        timePerTask:
                                                                            inputValue === ""
                                                                                ? 0
                                                                                : parseFloat(
                                                                                      inputValue
                                                                                  ) || 0,
                                                                    };
                                                                    setFieldValue(
                                                                        "taskConfiguration",
                                                                        updatedTasks
                                                                    );
                                                                    handleFormFieldChange(
                                                                        "taskConfiguration",
                                                                        updatedTasks
                                                                    );
                                                                }}
                                                                onBlur={(
                                                                    e: React.FocusEvent<HTMLInputElement>
                                                                ) => {
                                                                    // On blur, ensure we have a valid number
                                                                    const inputValue =
                                                                        e.target.value;
                                                                    const numericValue =
                                                                        inputValue === "" ||
                                                                        isNaN(
                                                                            parseFloat(inputValue)
                                                                        )
                                                                            ? 0
                                                                            : parseFloat(
                                                                                  inputValue
                                                                              );

                                                                    const updatedTasks = [
                                                                        ...(values.taskConfiguration ||
                                                                            []),
                                                                    ];
                                                                    updatedTasks[index] = {
                                                                        ...updatedTasks[index],
                                                                        timePerTask: numericValue,
                                                                    };
                                                                    setFieldValue(
                                                                        "taskConfiguration",
                                                                        updatedTasks
                                                                    );
                                                                    handleFormFieldChange(
                                                                        "taskConfiguration",
                                                                        updatedTasks
                                                                    );
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )
                                            )}

                                            <ErrorMessage
                                                name="taskConfiguration"
                                                component={FormErrorMessage}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <footer className="glu-modal__footer">
                        <div className="glu-flex-grow glu-flex-row">
                            <div className="text-right" style={{width: "100%"}}>
                                <button
                                    onClick={onCancel}
                                    className="glu-btn glu-btn--secondary"
                                    type="button"
                                    style={{marginRight: "10px"}}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="glu-btn glu-btn--primary"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </div>
                    </footer>
                </Form>
            )}
        </Formik>
    );
};

export default ROIConfigurationForm;
